<script setup lang="ts">
import { computed } from "vue";
import { funFacts, rankTitle, statsFor } from "../../domain/stats.js";
import type { Game } from "../../domain/store.js";

/**
 * Statistics, PRD FR-6.
 *
 * Everything is derived from the stored turn logs each time this renders, so a
 * deleted game disappears from every figure at once with no running totals to
 * fall out of step.
 */

const props = defineProps<{
  title: string;
  games: Game[];
  names: Record<string, string>;
  /** Roster ids, so players with no games still appear (FR-1.6). */
  playerIds: string[];
}>();

const emit = defineEmits<{ back: [] }>();

const rows = computed(() => statsFor(props.games, props.playerIds));
const facts = computed(() => funFacts(props.games, props.names));
const nameOf = (id: string): string => props.names[id] ?? id;
const MEDALS = ["🥇", "🥈", "🥉"];
</script>

<template>
  <div class="screen">
    <header class="nav">
      <button
        class="nav-btn"
        @click="emit('back')"
      >
        ‹ Zurück
      </button>
      <span class="title">{{ title }}</span>
      <span class="spacer" />
    </header>

    <div class="scroll">
      <p
        v-if="games.length === 0"
        class="empty"
      >
        Noch keine beendeten Spiele. Sobald eine Runde durch ist, steht hier die Bilanz.
      </p>

      <template v-else>
        <section class="facts">
          <div
            v-for="f in facts"
            :key="f.label"
            class="fact"
          >
            <span class="fact-icon">{{ f.icon }}</span>
            <span class="fact-body">
              <span class="fact-label">{{ f.label }}</span>
              <span class="fact-sub">{{ f.sub }}</span>
            </span>
            <span class="fact-value num">{{ f.value }}</span>
          </div>
        </section>

        <h2 class="h2">
          Tabelle
        </h2>
      </template>

      <div class="table-wrap">
        <table
          v-if="rows.length"
          class="table"
        >
          <thead>
            <tr>
              <th class="c-rank" />
              <th class="c-name">
                Spieler
              </th>
              <th class="c-num">
                Siege
              </th>
              <th class="c-num">
                Quote
              </th>
              <th class="c-num">
                Ø
              </th>
              <th class="c-num">
                Best
              </th>
              <th class="c-title">
                Rang
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(r, i) in rows"
              :key="r.playerId"
            >
              <td class="c-rank">
                {{ MEDALS[i] ?? i + 1 }}
              </td>
              <td class="c-name">
                {{ nameOf(r.playerId) }}
              </td>
              <td class="c-num num">
                {{ r.wins }}/{{ r.games }}
              </td>
              <td class="c-num num">
                {{ r.winRate }} %
              </td>
              <td class="c-num num">
                {{ r.average.toLocaleString("de-DE") }}
              </td>
              <td class="c-num num">
                {{ r.best.toLocaleString("de-DE") }}
              </td>
              <td class="c-title">
                {{ rankTitle(r.wins) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <template v-if="games.length > 0 && rows.length">
        <h2 class="h2">
          Striche &amp; Strafen
        </h2>
        <table class="table">
          <thead>
            <tr>
              <th class="c-name">
                Spieler
              </th>
              <th class="c-num">
                Striche
              </th>
              <th class="c-num">
                Quote
              </th>
              <th class="c-num">
                Strafen
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="r in rows"
              :key="r.playerId"
            >
              <td class="c-name">
                {{ nameOf(r.playerId) }}
              </td>
              <td class="c-num num">
                {{ r.strikes }}
              </td>
              <td class="c-num num">
                {{ r.strikeRate }} %
              </td>
              <td class="c-num num">
                {{ r.penalties }}
              </td>
            </tr>
          </tbody>
        </table>
      </template>
    </div>
  </div>
</template>

<style scoped>
.screen {
  height: 100dvh;
  display: flex;
  flex-direction: column;
  padding-left: max(12px, var(--safe-left));
  padding-right: max(12px, var(--safe-right));
  padding-bottom: calc(var(--safe-bottom) + 8px);
}

.nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: calc(var(--safe-top) + 6px) 0 6px;
  min-height: 52px;
}

.nav-btn {
  background: none;
  border: none;
  color: var(--accent);
  font-size: 15px;
  padding: 8px 4px;
  min-height: 44px;
}

.title {
  font-size: 15px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.spacer {
  width: 56px;
}

.scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.empty {
  color: var(--text-2);
  font-size: 14px;
  line-height: 1.5;
  margin: 12px 4px;
}

.facts {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fact {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--card);
  border-radius: var(--radius-card);
  padding: 10px 14px;
  min-height: 52px;
}

.fact-icon {
  font-size: 20px;
}

.fact-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.fact-label {
  font-size: 14px;
  font-weight: 600;
}

.fact-sub {
  font-size: 12px;
  color: var(--text-2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fact-value {
  font-size: 17px;
  font-weight: 700;
  color: var(--accent);
}

.h2 {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-2);
  margin: 18px 4px 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* The table can outgrow a narrow phone; it scrolls inside its own container
   rather than pushing the page sideways. */
.table-wrap {
  overflow-x: auto;
  border-radius: var(--radius-card);
}

.table {
  width: 100%;
  border-collapse: collapse;
  background: var(--card);
  border-radius: var(--radius-card);
  overflow: hidden;
  font-size: 14px;
}

th,
td {
  padding: 9px 8px;
  text-align: right;
}

th {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-2);
}

tbody tr + tr td {
  border-top: 1px solid var(--separator);
}

.c-rank {
  width: 2em;
  text-align: center;
  color: var(--text-3);
}

.c-name {
  text-align: left;
  max-width: 0;
  width: 40%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.c-num {
  white-space: nowrap;
}

.c-title {
  text-align: right;
  white-space: nowrap;
  font-size: 12px;
  color: var(--accent);
}
</style>
